import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, MapPin, Navigation, Flag, Plus, X } from 'lucide-react';

interface StopLocation {
  id: string;
  name: string;
  coordinates: [number, number];
  type: 'pickup' | 'enroute' | 'dropoff';
}

interface RideStopLocationStepProps {
  from: string;
  to: string;
  selectedStops: StopLocation[];
  onStopsSelected: (stops: StopLocation[]) => void;
  onBack: () => void;
  onComplete: () => void;
}

type StepType = 'pickup' | 'enroute' | 'dropoff';

export const RideStopLocationStep: React.FC<RideStopLocationStepProps> = ({
  from,
  to,
  selectedStops,
  onStopsSelected,
  onBack,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState<StepType>('pickup');
  const [stops, setStops] = useState<StopLocation[]>(selectedStops);
  const [suggestedStops, setSuggestedStops] = useState<StopLocation[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStopName, setNewStopName] = useState('');

  // Mock suggested stops based on route
  useEffect(() => {
    const mockSuggestions: StopLocation[] = [
      { id: '1', name: 'Metro Station', coordinates: [72.8777, 19.0760], type: 'pickup' },
      { id: '2', name: 'Mall Entrance', coordinates: [72.8850, 19.0750], type: 'pickup' },
      { id: '3', name: 'Bus Stop', coordinates: [72.8900, 19.0730], type: 'pickup' },
      { id: '4', name: 'Highway Rest Stop', coordinates: [72.9000, 19.0700], type: 'enroute' },
      { id: '5', name: 'Fuel Station', coordinates: [72.9100, 19.0680], type: 'enroute' },
      { id: '6', name: 'Food Court', coordinates: [72.9200, 19.0650], type: 'enroute' },
      { id: '7', name: 'Airport Terminal', coordinates: [72.9300, 19.0600], type: 'dropoff' },
      { id: '8', name: 'City Center', coordinates: [72.9350, 19.0580], type: 'dropoff' },
      { id: '9', name: 'Railway Station', coordinates: [72.9400, 19.0550], type: 'dropoff' },
    ];
    
    setSuggestedStops(mockSuggestions.filter(stop => stop.type === currentStep));
  }, [currentStep]);

  const handleStopSelect = (stop: StopLocation) => {
    const updatedStops = [...stops];
    const existingIndex = updatedStops.findIndex(s => s.id === stop.id);
    
    if (existingIndex >= 0) {
      updatedStops.splice(existingIndex, 1);
    } else {
      updatedStops.push(stop);
    }
    
    setStops(updatedStops);
  };

  const handleAddCustomStop = () => {
    if (!newStopName.trim()) return;
    
    const newStop: StopLocation = {
      id: `custom-${Date.now()}`,
      name: newStopName.trim(),
      coordinates: [72.8777 + Math.random() * 0.1, 19.0760 + Math.random() * 0.1],
      type: currentStep
    };
    
    setStops(prev => [...prev, newStop]);
    setNewStopName('');
    setShowAddForm(false);
  };

  const removeStop = (stopId: string) => {
    setStops(prev => prev.filter(s => s.id !== stopId));
  };

  const getStepIcon = (step: StepType) => {
    switch (step) {
      case 'pickup': return <MapPin className="w-4 h-4" />;
      case 'enroute': return <Navigation className="w-4 h-4" />;
      case 'dropoff': return <Flag className="w-4 h-4" />;
    }
  };

  const getStepColor = (step: StepType) => {
    switch (step) {
      case 'pickup': return 'bg-green-500';
      case 'enroute': return 'bg-yellow-500';
      case 'dropoff': return 'bg-red-500';
    }
  };

  const getCurrentStepStops = () => stops.filter(stop => stop.type === currentStep);

  const handleNext = () => {
    if (currentStep === 'pickup') {
      setCurrentStep('enroute');
    } else if (currentStep === 'enroute') {
      setCurrentStep('dropoff');
    } else {
      onStopsSelected(stops);
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep === 'pickup') {
      onBack();
    } else if (currentStep === 'enroute') {
      setCurrentStep('pickup');
    } else {
      setCurrentStep('enroute');
    }
  };

  return (
    <div className="fixed inset-0 z-[102] bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <Button variant="ghost" onClick={handleBack} size="sm" className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-semibold text-gray-800">Select Ride Stops</h2>
        <div className="w-10"></div>
      </div>

      {/* Step Indicator */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="flex justify-center space-x-2 mb-2">
          {(['pickup', 'enroute', 'dropoff'] as StepType[]).map((step, index) => (
            <div
              key={step}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                step === currentStep ? getStepColor(step) : 'bg-gray-300'
              }`}
            >
              {getStepIcon(step)}
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-600">
          Step {currentStep === 'pickup' ? '1' : currentStep === 'enroute' ? '2' : '3'} of 3: 
          Select {currentStep === 'pickup' ? 'Pickup' : currentStep === 'enroute' ? 'En-route' : 'Drop-off'} Stops (Optional)
        </p>
      </div>

      {/* Route Info */}
      <div className="p-4 bg-blue-50 border-b">
        <div className="text-sm">
          <span className="font-medium text-blue-800">Route: </span>
          <span className="text-blue-700">{from} → {to}</span>
        </div>
      </div>

      {/* Map removed */}
      <div className="flex-1 relative">
        {/* Selected Stops Overlay */}
        <div className="absolute bottom-4 left-4 right-4">
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  {currentStep === 'pickup' ? 'Pickup' : currentStep === 'enroute' ? 'En-route' : 'Drop-off'} Stops
                </span>
                <span className="text-xs text-gray-500">
                  {getCurrentStepStops().length} selected
                </span>
              </div>
              
              {/* Suggested Stops */}
              {suggestedStops.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-2">Suggested stops:</p>
                  <div className="flex flex-wrap gap-1">
                    {suggestedStops.slice(0, 3).map(stop => (
                      <Button
                        key={stop.id}
                        variant={stops.find(s => s.id === stop.id) ? "default" : "outline"}
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => handleStopSelect(stop)}
                      >
                        {stop.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Stop Addition */}
              {!showAddForm ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mb-2"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Custom Stop
                </Button>
              ) : (
                <div className="mb-2 p-2 border rounded">
                  <input
                    type="text"
                    value={newStopName}
                    onChange={(e) => setNewStopName(e.target.value)}
                    placeholder="Enter stop name"
                    className="w-full px-2 py-1 text-xs border rounded mb-2"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" onClick={handleAddCustomStop}>
                      Add
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      setShowAddForm(false);
                      setNewStopName('');
                    }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Selected Stops List */}
              {getCurrentStepStops().length > 0 && (
                <div className="space-y-1 mb-3 max-h-20 overflow-y-auto">
                  {getCurrentStepStops().map(stop => (
                    <div key={stop.id} className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1 rounded">
                      <span>{stop.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0"
                        onClick={() => removeStop(stop.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                onClick={handleNext}
                className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white py-2 text-sm"
              >
                {currentStep === 'dropoff' ? 'Complete Selection' : 'Next Step'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
